import React from 'react';
import styled from 'styled-components';

const RESERVATION_TTL_SECONDS = 600;
const WARNING_THRESHOLD_SECONDS = Math.round(RESERVATION_TTL_SECONDS / 3);
const CAUTION_THRESHOLD_SECONDS = Math.round((RESERVATION_TTL_SECONDS * 2) / 3);

const TimerContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  box-shadow: 0 4px 16px rgba(0,0,0,0.1);
  border-left: 4px solid ${props => {
        if (props.timeRemaining <= 300) return '#ef4444'; // Red for ≤5 min
        if (props.timeRemaining <= 600) return '#f59e0b'; // Orange for ≤10 min
        return '#10b981'; // Green for >10 min
    }};
`;

const TimerHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  
  .icon {
    font-size: 1.2rem;
  }
  
  h3 {
    margin: 0;
    color: #333;
    font-size: 1rem;
    font-weight: 600;
  }
`;

const TimerDisplay = styled.div`
  text-align: center;
  
  .time {
    font-size: 2rem;
    font-weight: 700;
    color: ${props => {
        if (props.timeRemaining <= 300) return '#ef4444'; // Red for ≤5 min
        if (props.timeRemaining <= 600) return '#f59e0b'; // Orange for ≤10 min
        return '#10b981'; // Green for >10 min
    }};
    margin-bottom: 0.25rem;
    font-family: 'Monaco', 'Menlo', monospace;
  }
  
  .label {
    font-size: 0.85rem;
    color: #666;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 500;
  }
`;

const WarningMessage = styled.div`
  margin-top: 1rem;
  padding: 0.75rem;
  border-radius: 6px;
  font-size: 0.85rem;
  line-height: 1.4;
  
  ${props => {
        if (props.timeRemaining <= WARNING_THRESHOLD_SECONDS) {
            return `
        background: #fef2f2;
        color: #dc2626;
        border: 1px solid #fecaca;
      `;
        }
        if (props.timeRemaining <= CAUTION_THRESHOLD_SECONDS) {
            return `
        background: #fffbeb;
        color: #d97706;
        border: 1px solid #fed7aa;
      `;
        }
        return `
      background: #f0fdf4;
      color: #16a34a;
      border: 1px solid #bbf7d0;
    `;
    }}
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background: #e5e7eb;
  border-radius: 3px;
  margin-top: 1rem;
  overflow: hidden;
  
  .progress {
    height: 100%;
    background: ${props => {
        if (props.timeRemaining <= WARNING_THRESHOLD_SECONDS) return '#ef4444';
        if (props.timeRemaining <= CAUTION_THRESHOLD_SECONDS) return '#f59e0b';
        return '#10b981';
    }};
    width: ${props => Math.max(0, Math.min(100, (props.timeRemaining / RESERVATION_TTL_SECONDS) * 100))}%;
    transition: width 1s linear;
    border-radius: 3px;
  }
`;

const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
};

const getWarningMessage = (timeRemaining) => {
    if (timeRemaining <= WARNING_THRESHOLD_SECONDS) {
        return "⚠️ Your reservation will expire soon! Please complete your purchase quickly.";
    }
    if (timeRemaining <= CAUTION_THRESHOLD_SECONDS) {
        return "⏰ Please complete your purchase soon to secure your tickets.";
    }
    return "✅ Your tickets are reserved. Take your time to complete the purchase.";
};

const TimerCountdown = ({ expiresAt, timeRemaining }) => {
    if (!expiresAt || timeRemaining <= 0) {
        return null;
    }

    return (
        <TimerContainer timeRemaining={timeRemaining}>
            <TimerHeader>
                <span className="icon">⏱️</span>
                <h3>Reservation Timer</h3>
            </TimerHeader>

            <TimerDisplay timeRemaining={timeRemaining}>
                <div className="time">{formatTime(timeRemaining)}</div>
                <div className="label">Time Remaining</div>
            </TimerDisplay>

            <ProgressBar timeRemaining={timeRemaining}>
                <div className="progress"></div>
            </ProgressBar>

            <WarningMessage timeRemaining={timeRemaining}>
                {getWarningMessage(timeRemaining)}
            </WarningMessage>
        </TimerContainer>
    );
};

export default TimerCountdown;
